/**
 * iter317 — Phase 7B: Plan-gating Upgrade modal.
 *
 * Listens on the global `upgradePrompt` event bus and renders a
 * shadcn Dialog explaining the missing feature. Mounted once near
 * the app shell (App.js) so any part of the tree — including the
 * axios interceptor — can request it.
 */
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Sparkles, Lock } from 'lucide-react';
import { subscribeUpgradePrompt } from '../lib/upgradePrompt';

// Human-friendly labels for our internal feature keys. Keep in sync
// with backend `feature_catalog.py:FEATURES`. Unknown keys fall back
// to a Title Case rendering of the key itself.
const FEATURE_LABELS = {
  'tournaments.registrations': 'Tournaments',
  'tournaments.games': 'Tournament Games & RSVPs',
  'tournaments.brackets': 'Tournament Brackets',
  'bookings.lanes': 'Lane Bookings',
  'bookings.small_groups': 'Small Group Sessions',
  'bookings.coaching': '1:1 / Private Coaching',
  'camps.core': 'Camps',
  'events.core': 'Events',
  'comms.email': 'Email Communications',
  'comms.whatsapp': 'WhatsApp Communications',
  'comms.campaigns': 'Multi-channel Campaigns',
  'content.blog': 'Blog / CMS',
  'content.forms': 'Forms & Waivers',
  'enrollments.core': 'Enrollments',
  'enrollments.monthly_billing': 'Monthly Billing',
  'enrollments.manual_cycles': 'Manual Billing Cycles',
  'payments.module': 'Payments Module',
  'payments.stripe_byok': 'Stripe Payments',
  'payments.ach': 'ACH Bank Payments',
  'payments.surcharges': 'Card Surcharges',
  'payments.refunds': 'Refunds',
  'payments.account_credit': 'Account Credit Ledger',
  'programs.catalog': 'Programs',
  'programs.classes': 'Classes',
  'programs.coaches': 'Coach Management',
  'programs.attendance': 'Attendance',
  'core.staff': 'Staff Management',
};

const labelFor = (key) => FEATURE_LABELS[key] || (key || '').replace(/\./g, ' › ').replace(/_/g, ' ');

export default function UpgradeModal() {
  const [open, setOpen] = useState(false);
  const [featureKey, setFeatureKey] = useState(null);
  const [actionLabel, setActionLabel] = useState(null);

  useEffect(() => {
    const unsub = subscribeUpgradePrompt(({ featureKey: k, actionLabel: a }) => {
      setFeatureKey(k);
      setActionLabel(a || null);
      setOpen(true);
    });
    return unsub;
  }, []);

  const friendlyName = labelFor(featureKey);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md" data-testid="upgrade-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-amber-600" />
            Upgrade required
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-3">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm font-medium text-amber-900 flex items-start gap-2">
              <Sparkles className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                {actionLabel ? (
                  <>
                    <strong>{actionLabel}</strong> requires the{' '}
                  </>
                ) : (
                  <>This area requires the{' '}</>
                )}
                <strong className="text-amber-950">{friendlyName}</strong> module, which isn&apos;t on your current plan.
              </span>
            </p>
          </div>

          <p className="text-sm text-slate-600">
            Reach out to your account manager (or your platform super-admin) to enable this feature.
            Once your plan is upgraded, refresh this page and you&apos;re good to go.
          </p>

          {featureKey && (
            <p className="text-xs text-slate-400 font-mono">
              feature_key: {featureKey}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="upgrade-modal-close">
            Close
          </Button>
          <Button
            onClick={() => {
              setOpen(false);
              window.location.href = 'mailto:support@vyomos.io?subject=Plan upgrade request: ' + (featureKey || 'unknown');
            }}
            data-testid="upgrade-modal-contact"
          >
            Contact support
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
